/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/EntryLiveRow.tsx
 * FILE_ID: UI-KANBAN-ENTRIES-ROW-001
 * PURPOSE: One SAVED Entries-table row — a real entry sub-card with
 *          done-toggle, teammate/date/amount cells, ←/→ reorder,
 *          chevron expand (opens the sub-card's own drawer) and ×
 *          dismiss (archives the sub-card).
 * WHY: ARC-3 (U17) page-10 sketch: "Rows have × dismiss, ←/→
 *      move/reorder, expand chevrons."
 * RELATED:
 *   - CardEntriesSection.tsx: the host table
 *   - useCardEntries.ts: the mutations these buttons call
 */

import { ArrowDown, ArrowUp, ChevronRight, X } from 'lucide-react'
import type { KanbanMember } from '../../../api'
import type { Card } from '../../../types'

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return ''
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`
}

export function EntryLiveRow({
  entry,
  index,
  count,
  memberById,
  busy,
  onSetDone,
  onDismiss,
  onReorder,
  onOpen,
}: {
  entry: Card
  index: number
  count: number
  memberById: Map<number, KanbanMember>
  busy: boolean
  onSetDone: (entry: Card, done: boolean) => void
  onDismiss: (entry: Card) => void
  onReorder: (entry: Card, dir: -1 | 1) => void
  onOpen?: (cardId: string) => void
}) {
  const done = entry.status === 'DONE'
  const assignee = (entry.assigned_user_ids ?? [])
    .map((id) => memberById.get(id)?.name)
    .filter(Boolean)
    .join(', ')

  return (
    <tr
      data-testid={`entry-row-${entry.id}`}
      className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60"
    >
      <td className="px-1.5 py-1">
        <input
          type="checkbox"
          checked={done}
          disabled={busy}
          onChange={(e) => onSetDone(entry, e.target.checked)}
          aria-label={done ? 'Reopen entry' : 'Mark entry done'}
          className="h-3.5 w-3.5 accent-emerald-600"
        />
      </td>
      <td className="max-w-[110px] truncate px-1.5 py-1 text-[11.5px] text-neutral-600 dark:text-neutral-300">
        {assignee || '—'}
      </td>
      <td
        className={[
          'px-1.5 py-1 text-[12px] text-neutral-800 dark:text-neutral-100',
          done ? 'line-through opacity-60' : '',
        ].join(' ')}
      >
        {entry.title || '(untitled)'}
      </td>
      <td className="whitespace-nowrap px-1.5 py-1 text-[11.5px] text-neutral-600 dark:text-neutral-300">
        {entry.action_date ?? ''}
      </td>
      <td className="whitespace-nowrap px-1.5 py-1 text-right text-[11.5px] tabular-nums text-neutral-700 dark:text-neutral-200">
        {formatCents(entry.amount_cents)}
      </td>
      <td className="whitespace-nowrap px-1 py-1 text-right">
        <span className="inline-flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onReorder(entry, -1)}
            disabled={busy || index === 0}
            aria-label="Move entry up"
            className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-30 dark:hover:bg-neutral-800"
          >
            <ArrowUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onReorder(entry, 1)}
            disabled={busy || index === count - 1}
            aria-label="Move entry down"
            className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-30 dark:hover:bg-neutral-800"
          >
            <ArrowDown className="h-3 w-3" />
          </button>
          {onOpen && (
            <button
              type="button"
              onClick={() => onOpen(entry.id)}
              aria-label="Open entry as a card"
              title="Open this entry — it's a real sub-card"
              className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDismiss(entry)}
            disabled={busy}
            aria-label="Dismiss entry (archives the sub-card)"
            className="rounded p-0.5 text-neutral-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-950/40"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      </td>
    </tr>
  )
}
